/**
 * EmptyState — one presentation for "there is nothing here".
 *
 * A shared component rather than a block written into each screen: the canvas files this under
 * *"Shared components · not a route"*, with the note that there is one presentation for each
 * condition, reused everywhere rather than reinvented per screen. Later screens are specified to
 * reuse it.
 *
 * It is told what to say and never works out *why* a collection is empty. That restraint is a
 * security property, not a simplification: an empty collection must read the same whether nothing
 * was ever shared or everything was revoked, because saying which discloses that a private Liturgy
 * exists — the disclosure the 404-not-403 rule exists to prevent
 * (docs/engineering/04-authorization.md).
 *
 * A primitive knows nothing about the domain, so the action arrives as a node. The caller supplies
 * a real routed link; this file never learns what it points at.
 */

import type { ReactNode } from 'react';

import './primitives.css';

export interface EmptyStateProps {
  /**
   * Decorative artwork. `alt` is always empty by construction — the heading already carries the
   * meaning, and a description of the shield would only be read out twice.
   */
  illustration?: string;
  heading: string;
  body: string;
  /** Usually a link to the one thing that would end the empty state. */
  action?: ReactNode;
  /**
   * The same state at aside scale: left-aligned, tightly spaced, smaller heading.
   *
   * A variant rather than a second component, because the alternative is a bespoke "nothing here"
   * block written into each narrow panel — which is how one presentation per condition quietly
   * becomes four. The words and the semantics are unchanged; only the room it is given differs.
   */
  compact?: boolean;
}

export function EmptyState({
  illustration,
  heading,
  body,
  action,
  compact = false,
}: EmptyStateProps): React.JSX.Element {
  return (
    <div className={`empty${compact ? ' empty--compact' : ''}`}>
      {illustration ? <img className="empty__art" src={illustration} alt="" /> : null}
      <p className="empty__heading">{heading}</p>
      <p className="empty__body">{body}</p>
      {action ? <div className="empty__action">{action}</div> : null}
    </div>
  );
}
